import type { FC, ReactNode } from 'react'
import type { BoardTheme } from '@/constants/board-themes'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

import { BOARD_THEMES, DEFAULT_BOARD_THEME_ID } from '@/constants/board-themes'

const STORAGE_KEY = 'neon-gambit:board-theme'

interface BoardThemeContextValue {
    theme: BoardTheme
    themes: BoardTheme[]
    setThemeId: (id: string) => void
}

interface BoardThemeProviderProps {
    children: ReactNode
}

const BoardThemeContext = createContext<BoardThemeContextValue | null>(null)

export const BoardThemeProvider: FC<BoardThemeProviderProps> = ({ children }): ReactNode => {
    const [themeId, setThemeIdState] = useState(DEFAULT_BOARD_THEME_ID)

    useEffect(() => {
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY)
            if (saved && BOARD_THEMES.some(theme => theme.id === saved)) setThemeIdState(saved)
        }
        catch {
            // Storage can be blocked in private browsing; the default theme stays.
        }
    }, [])

    const setThemeId = useCallback((id: string) => {
        setThemeIdState(id)

        try {
            window.localStorage.setItem(STORAGE_KEY, id)
        }
        catch {
            // Saving the choice is a nice-to-have, never a blocker.
        }
    }, [])

    const value = useMemo<BoardThemeContextValue>(() => ({
        theme: BOARD_THEMES.find(theme => theme.id === themeId) ?? BOARD_THEMES[0],
        themes: BOARD_THEMES,
        setThemeId
    }), [themeId, setThemeId])

    return (
        <BoardThemeContext.Provider value={value}>
            {children}
        </BoardThemeContext.Provider>
    )
}

export const useBoardTheme = (): BoardThemeContextValue => {
    const context = useContext(BoardThemeContext)
    if (!context) throw new Error('useBoardTheme must be used inside BoardThemeProvider')
    return context
}
